import { formatarAteDuasCasas, unidadeDeConsumo, type UnidadeDeConsumo } from '@/lib/formato';

import type { Abastecimento, EquipamentoDoModulo, FaixaConsumo } from './tipos';
import { totalDoAbastecimento } from './tipos';

/**
 * A conta que faz esta tela existir: quanto aquela maquina consumiu por hora
 * entre um abastecimento e o seguinte.
 *
 * Tudo aqui e funcao pura, sem React e sem fixture. Quem serve dado e
 * `consultas.ts`; quem desenha e a pagina. O motivo nao e purismo: a regra do
 * horimetro e a do consumo sao as duas unicas coisas desta tela que produzem um
 * numero que alguem vai usar para cobrar alguem, e elas precisam poder ser
 * conferidas sem abrir navegador.
 *
 * NOMENCLATURA
 * "Anterior" e sempre o abastecimento anterior DO MESMO EQUIPAMENTO E DO MESMO
 * PRODUTO, em ordem de data. Consumo de frota nao existe: 240 L num caminhao e
 * 240 L numa escavadeira nao sao o mesmo fato. Consumo de produto misturado
 * tambem nao: 20 L de ARLA entre dois abastecimentos de diesel nao movem o L/h
 * do diesel — e foi para isso que a migracao 0011 separou a cadeia no banco.
 */

export const PENDENCIA_SEM_OPERADOR = 'Sem operador';
export const PENDENCIA_SEM_CENTRO_DE_CUSTO = 'Sem centro de custo';
export const PENDENCIA_CONSUMO_FORA_DA_FAIXA = 'Consumo fora da faixa';

/** Um abastecimento com o que se pode deduzir dele. */
export interface Lancamento {
  readonly abastecimento: Abastecimento;
  /** O abastecimento anterior do mesmo equipamento E do mesmo produto. */
  readonly anterior: Abastecimento | null;
  /** Horas de horimetro entre os dois. `null` quando nao da para calcular. */
  readonly horas: number | null;
  /** Litros por hora ou por km, conforme `unidade`. `null` quando nao ha anterior ou a diferenca nao e positiva. */
  readonly consumo: number | null;
  /**
   * `L/h` com horimetro, `L/km` com odometro. Vem da leitura do lancamento e,
   * sem ela, do cadastro da maquina. Caminhao escrito em "L/h" era o defeito.
   */
  readonly unidade: UnidadeDeConsumo;
  readonly tipo: string | null;
  readonly faixa: FaixaConsumo | null;
  /** O que falta ou nao fecha neste lancamento. Vazio quando esta tudo certo. */
  readonly pendencias: readonly string[];
}

/**
 * Horas rodadas desde o abastecimento anterior.
 *
 * Devolve `null` — e nao zero, e nao negativo — quando a diferenca nao e
 * positiva. Zero hora com litros dentro daria divisao por zero; diferenca
 * negativa e horimetro digitado errado, e ai a resposta certa e "nao sei", nao
 * um numero com sinal trocado.
 */
export function horasDesde(horimetro: number | null, anterior: Abastecimento | null): number | null {
  if (horimetro === null) return null;
  if (anterior === null || anterior.horimetro === null) return null;
  const horas = horimetro - anterior.horimetro;
  return horas > 0 ? horas : null;
}

export function consumoDe(litros: number, horas: number | null): number | null {
  if (horas === null || horas <= 0 || litros <= 0) return null;
  return litros / horas;
}

/**
 * Fora da faixa esperada.
 *
 * Sem faixa cadastrada a resposta e `false`, nunca `true`: o sistema nao sabe, e
 * "nao sei" nao e "esta errado". Avisar sobre uma maquina cujo consumo normal
 * ninguem definiu treinaria a pessoa a ignorar o aviso.
 *
 * CADA PONTA SOZINHA JA JULGA, e esta e a regra do servidor copiada ponta a
 * ponta (`comb.consumo_abastecimento`, migracao 0011): so teto acusa consumo
 * acima dele, so piso acusa abaixo. Exigir o par fazia a tela ficar muda
 * justamente onde o banco ia marcar desvio — o formulario nao avisava antes de
 * salvar e a lista marcava a mesma linha logo depois.
 */
export function estaForaDaFaixa(consumo: number | null, faixa: FaixaConsumo | null): boolean {
  if (consumo === null || faixa === null) return false;
  return (
    (faixa.minimo !== null && consumo < faixa.minimo) ||
    (faixa.maximo !== null && consumo > faixa.maximo)
  );
}

/**
 * De que lado o consumo saiu da faixa.
 *
 * So faz sentido depois de `estaForaDaFaixa` dizer que saiu. Com faixa pela
 * metade a resposta e a unica possivel: sem teto nao ha como estar acima.
 */
export function ladoDoDesvio(consumo: number, faixa: FaixaConsumo): 'acima' | 'abaixo' {
  return faixa.maximo !== null && consumo > faixa.maximo ? 'acima' : 'abaixo';
}

/**
 * A faixa escrita para quem le a tela.
 *
 * Mora aqui, e nao em cada tela, porque sao TRES que a escrevem — o formulario,
 * a lista de consumo e a tela de lancamento — e a faixa pela metade e
 * exatamente o caso que cada uma escreveria de um jeito. "2 a 6" quando ha as
 * duas pontas; "até 6" e "a partir de 2" quando ha uma so.
 *
 * Devolve `null` quando nao ha faixa nenhuma: o estado vazio e de quem desenha,
 * que o escreve com o tom e a cor da sua tela.
 */
export function textoDaFaixa(
  minimo: number | null,
  maximo: number | null,
  /** `L/h`, `L/km` — omitida quando a coluna ja declara a unidade no cabecalho. */
  unidade?: string,
): string | null {
  const sufixo = unidade === undefined ? '' : ` ${unidade}`;
  /* AS CASAS QUE A FAIXA TEM, ate duas (o banco guarda `numeric(8,2)`).
     `formatarInteiro` arredondava a faixa de 0,3 a 0,6 L/km de um caminhao
     para "0 a 1", e o aviso chegava a dizer que 0,0 L/km estava abaixo dela. */
  if (minimo !== null && maximo !== null) {
    return `${formatarAteDuasCasas(minimo)} a ${formatarAteDuasCasas(maximo)}${sufixo}`;
  }
  if (maximo !== null) return `até ${formatarAteDuasCasas(maximo)}${sufixo}`;
  if (minimo !== null) return `a partir de ${formatarAteDuasCasas(minimo)}${sufixo}`;
  return null;
}

/**
 * A categoria de produto que a conta de litros por hora descreve.
 *
 * O mesmo criterio do banco (`comb.consumo_abastecimento`, migracao 0011): a
 * faixa cadastrada e de litro de COMBUSTIVEL por hora.
 */
const CATEGORIA_DE_COMBUSTIVEL = 'combustivel';

/**
 * Se este abastecimento entra na media de litros por hora da maquina.
 *
 * ARLA nao entra, e o motivo nao e de gosto: ele tem cadeia propria no banco,
 * com o proprio intervalo de medidor. Somar as duas cadeias numa media so poe
 * litros que nao sao do diesel no numerador E conta as mesmas horas duas vezes
 * no denominador — o numero que sai nao e o consumo de nada.
 *
 * Categoria AUSENTE entra: e a linha que nao passou pela API desta versao, e
 * exclui-la esvaziaria a coluna inteira contra um servidor mais velho.
 * Ausencia e "nao sei qual e", nao "nao e combustivel".
 */
export function entraNaMediaDeConsumo(abastecimento: Abastecimento): boolean {
  return (
    abastecimento.produtoCategoria === undefined ||
    abastecimento.produtoCategoria === CATEGORIA_DE_COMBUSTIVEL
  );
}

/** O abastecimento de antes e o de depois de um instante, no mesmo equipamento. */
export interface Vizinhos {
  /** O ultimo abastecimento ANTERIOR ao instante. E dele que sai a base do consumo. */
  readonly anterior: Abastecimento | null;
  /** O primeiro abastecimento POSTERIOR ao instante. `null` quando nao ha. */
  readonly proximo: Abastecimento | null;
}

/**
 * Os vizinhos de um lancamento na linha do tempo do equipamento.
 *
 * POR QUE NAO E "O MAIS RECENTE"
 * O campo Data e hora do formulario convida ao retroativo com todas as letras
 * ("Troque se o abastecimento foi antes"). Pegar o abastecimento de maior data
 * como "anterior" produz dois numeros errados de uma vez: o bloco de consumo
 * compara um lancamento de 16/09 com o de 19/09 e escreve "desde o
 * abastecimento de 19/09" — uma data no futuro do proprio lancamento —, e
 * depois de salvar a lista reordena por data e mostra um L/h diferente do que
 * o formulario mostrou antes do clique. Consumo e a diferenca entre DOIS
 * abastecimentos consecutivos; "consecutivo" e por data, nao por ordem de
 * digitacao.
 *
 * O `proximo` existe pelo outro lado do mesmo problema: um registro
 * intercalado com horimetro maior que o do abastecimento seguinte faz
 * `horasDesde` daquela linha devolver `null`, e o consumo de uma linha que
 * estava certa some da tabela.
 *
 * Empate de data cai para o `anterior`: dois abastecimentos no mesmo minuto sao
 * digitacao repetida, e tratar o lancamento novo como posterior e o que mantem
 * a leitura de horimetro validada contra a que ja existe.
 */
export function vizinhosDe(
  codigo: string,
  abastecimentos: readonly Abastecimento[],
  quando: Date | null,
): Vizinhos {
  if (!codigo) return { anterior: null, proximo: null };

  const limite = quando?.getTime() ?? Number.POSITIVE_INFINITY;
  let anterior: Abastecimento | null = null;
  let proximo: Abastecimento | null = null;

  for (const abastecimento of abastecimentos) {
    if (abastecimento.equipamentoCodigo !== codigo) continue;
    const instante = abastecimento.quando.getTime();

    if (instante <= limite) {
      if (anterior === null || instante > anterior.quando.getTime()) anterior = abastecimento;
    } else if (proximo === null || instante < proximo.quando.getTime()) {
      proximo = abastecimento;
    }
  }

  return { anterior, proximo };
}

/**
 * A maior leitura de horimetro ja registrada ATE a data do lancamento.
 *
 * Sao DUAS fontes, e elas nao sao a mesma: o cadastro do equipamento tem a
 * leitura mais recente (apontamento de hora, oficina), e o abastecimento
 * anterior tem a leitura daquele momento. Para um lancamento de hoje a
 * validacao usa a MAIOR das duas — horimetro nao anda para tras, e aceitar um
 * valor menor que qualquer registro existente e como o consumo de um mes
 * inteiro fica errado.
 *
 * PARA UM LANCAMENTO RETROATIVO A LEITURA DO CADASTRO NAO VALE
 * Ela e a leitura de HOJE e nao carrega data; usa-la como piso de um
 * abastecimento de tres dias atras recusa um lancamento legitimo com a
 * mensagem "o horimetro nao pode ser menor que o ultimo registro". Quando ha
 * abastecimento posterior a data digitada, o unico piso honesto e o horimetro
 * do abastecimento anterior.
 */
export function ultimoRegistroDeHorimetro(
  /** A ultima leitura registrada do equipamento — do cadastro ou do banco. */
  doCadastro: number | null,
  anterior: Abastecimento | null,
  /** `false` quando existe abastecimento posterior a data do lancamento. */
  ehOMaisRecente = true,
): number | null {
  if (!ehOMaisRecente) return anterior?.horimetro ?? null;
  if (anterior === null || anterior.horimetro === null) return doCadastro;
  if (doCadastro === null) return anterior.horimetro;
  return Math.max(doCadastro, anterior.horimetro);
}

export function pendenciasDe(
  abastecimento: Abastecimento,
  consumo: number | null,
  faixa: FaixaConsumo | null,
): readonly string[] {
  const pendencias: string[] = [];
  if (abastecimento.operador === null) pendencias.push(PENDENCIA_SEM_OPERADOR);
  if (abastecimento.centroCusto === null) pendencias.push(PENDENCIA_SEM_CENTRO_DE_CUSTO);
  /* O VEREDITO É DO SERVIDOR quando ele veio (migração 0011). O cálculo local
     continua atrás dele como queda, para a lista não perder a coluna enquanto
     um ambiente antigo não tiver a migração — mas ele não decide mais nada
     onde a resposta do banco existe.
     
     `=== true` de propósito: `null` é "não sei" e não pode virar pendência.
     Avisar sobre um consumo cuja faixa ninguém cadastrou treina a pessoa a
     ignorar o aviso, que é o oposto do que ele existe para fazer.

     `!== undefined`, e NÃO `??`: a queda é para o campo AUSENTE, nunca para o
     campo nulo. `null` é o servidor dizendo "não sei" — sem consumo apurado,
     sem faixa cadastrada, ou produto que a faixa não descreve. Com `??` esse
     "não sei" caía no cálculo local, que não conhece categoria de produto e
     compara o L/h do ARLA com a faixa do diesel: todo abastecimento de ARLA
     voltava marcado "Consumo fora da faixa", que é justamente o ruído que a
     0011 existe para tirar. */
  const desvio =
    abastecimento.foraDaFaixa !== undefined
      ? abastecimento.foraDaFaixa
      : estaForaDaFaixa(consumo, faixa);
  if (desvio === true) pendencias.push(PENDENCIA_CONSUMO_FORA_DA_FAIXA);
  return pendencias;
}

/**
 * A cadeia a que este abastecimento pertence: a máquina e o produto.
 *
 * O id do produto manda; o nome é a queda para a linha que veio de um servidor
 * sem ele. Sem nenhum dos dois, tudo cai na mesma cadeia — que é o que a tela
 * fazia antes da 0011.
 */
function cadeiaDe(abastecimento: Abastecimento): string {
  const produto = abastecimento.produtoId ?? abastecimento.produto ?? '';
  return `${abastecimento.equipamentoCodigo}\u0000${produto}`;
}

/**
 * Liga cada abastecimento ao anterior da mesma cadeia e calcula o resto.
 *
 * Devolve em ordem decrescente de data — o ultimo lancamento primeiro, que e o
 * que quem acabou de lancar quer conferir.
 */
export function calcularLancamentos(
  abastecimentos: readonly Abastecimento[],
  equipamentos: readonly EquipamentoDoModulo[],
): readonly Lancamento[] {
  const porCodigo = new Map(equipamentos.map((equipamento) => [equipamento.codigo, equipamento]));

  /* A CADEIA É POR MÁQUINA E POR PRODUTO, a mesma partição do banco.
     Encadear só por máquina fazia o "anterior" de um abastecimento de diesel
     ser o de ARLA que entrou no meio, e com ele as `horas` do intervalo —
     90 h onde o diesel andou 100. O consumo exibido vem do banco e estava
     certo; as horas, que são o denominador de toda média feita na tela,
     vinham de outra conta. Duas metades de fontes diferentes não fecham. */
  const porCadeia = new Map<string, Abastecimento[]>();
  for (const abastecimento of abastecimentos) {
    const chave = cadeiaDe(abastecimento);
    const lista = porCadeia.get(chave);
    if (lista) lista.push(abastecimento);
    else porCadeia.set(chave, [abastecimento]);
  }

  const anteriorPorId = new Map<string, Abastecimento>();
  for (const lista of porCadeia.values()) {
    lista.sort((um, outro) => um.quando.getTime() - outro.quando.getTime());
    for (let indice = 1; indice < lista.length; indice += 1) {
      const atual = lista[indice];
      const anterior = lista[indice - 1];
      if (atual && anterior) anteriorPorId.set(atual.id, anterior);
    }
  }

  return [...abastecimentos]
    .sort((um, outro) => outro.quando.getTime() - um.quando.getTime())
    .map((abastecimento) => {
      const anterior = anteriorPorId.get(abastecimento.id) ?? null;
      const horas = horasDesde(abastecimento.horimetro, anterior);
      const equipamento = porCodigo.get(abastecimento.equipamentoCodigo) ?? null;
      const tipo = equipamento?.tipo ?? null;
      const faixa = equipamento?.faixa ?? null;
      /* O MEDIDOR DA LEITURA manda, e o do cadastro e a queda: a view compara
         leituras do mesmo tipo de medidor, e a maquina que nao esta mais na
         lista de cadastros continua com a unidade certa. */
      const unidade = unidadeDeConsumo(abastecimento.tipoMedidor ?? equipamento?.tipoMedidor);
      /* O CONSUMO DO BANCO VENCE. `comb.consumo_abastecimento` ja fez a conta
         entre dois abastecimentos do mesmo ativo, com o mesmo tipo de medidor,
         e declarou a limitacao quando ela existe. Recalcular aqui produziria um
         segundo numero para a mesma pergunta — e dois numeros para a mesma
         pergunta divergem na primeira correcao. A conta local continua valendo
         para a massa de demonstracao, que nunca passou por view nenhuma. */
      const consumo =
        abastecimento.consumoDoBanco !== undefined
          ? abastecimento.consumoDoBanco
          : consumoDe(abastecimento.litros, horas);

      return {
        abastecimento,
        anterior,
        horas,
        consumo,
        unidade,
        tipo,
        faixa,
        pendencias: pendenciasDe(abastecimento, consumo, faixa),
      };
    });
}

export interface ResumoDoPeriodo {
  readonly quantidade: number;
  readonly litros: number;
  readonly custo: number;
  /**
   * Media PONDERADA em L/h: litros com base comparavel divididos pelas horas
   * deles — SO lancamentos de horimetro. Os de odometro tem a media propria.
   */
  readonly consumoMedio: number | null;
  /** Quantos lancamentos entraram na media em L/h — os de horimetro com leitura anterior. */
  readonly comBaseDeConsumo: number;
  /** Media PONDERADA em L/km: litros divididos pelos km — so lancamentos de odometro. */
  readonly consumoMedioPorKm: number | null;
  /** Quantos lancamentos entraram na media em L/km. */
  readonly comBaseDeConsumoPorKm: number;
  /** Quantos lancamentos entraram SEM preco. O custo nao os inclui, e a tela diz. */
  readonly semPreco: number;
  readonly comPendencia: number;
  readonly primeiro: Date | null;
  readonly ultimo: Date | null;
}

/**
 * Os quatro numeros que o gestor pergunta de manha, de uma vez so.
 *
 * O consumo medio e PONDERADO: soma dos litros dos lancamentos que tem base de
 * comparacao, dividida pela soma das horas deles. A media das medias daria peso
 * igual a um lancamento de 88 L e a um de 430 L, o que produz um numero errado
 * com cara de certo — o mesmo motivo pelo qual o preco medio do litro tambem e
 * ponderado.
 */
export function resumoDoPeriodo(lancamentos: readonly Lancamento[]): ResumoDoPeriodo {
  let litros = 0;
  let custo = 0;
  let litrosComBase = 0;
  let horasComBase = 0;
  let comBaseDeConsumo = 0;
  let litrosComBasePorKm = 0;
  let kmComBase = 0;
  let comBaseDeConsumoPorKm = 0;
  let comPendencia = 0;
  let semPreco = 0;
  let primeiro: Date | null = null;
  let ultimo: Date | null = null;

  for (const lancamento of lancamentos) {
    const { abastecimento, horas, consumo, unidade } = lancamento;
    litros += abastecimento.litros;
    /* Lancamento sem preco NAO entra no custo, e nao vira zero: tanque que
       ainda nao recebeu nota nao fez o diesel sair de graca. Quantos ficaram de
       fora e um numero que a tela mostra ao lado do total. */
    const total = totalDoAbastecimento(abastecimento);
    if (total === null) semPreco += 1;
    else custo += total;

    /* SÓ COMBUSTÍVEL ENTRA NA MÉDIA. O ARLA tem cadeia própria no banco, com
       o próprio intervalo de medidor: somá-lo aqui poria litros que não são do
       diesel em cima de horas contadas duas vezes. A linha continua na lista,
       nos litros e no custo — ela só não decide o L/h da máquina. */
    if (consumo !== null && entraNaMediaDeConsumo(abastecimento)) {
      /* OS LITROS DO INTERVALO, e nao os desta linha.
       *
       * `comb.consumo_abastecimento` divide os litros do intervalo inteiro
       * entre duas leituras pela variacao do medidor. Somar aqui so
       * `abastecimento.litros` sobre as mesmas horas e refazer a conta com o
       * numerador errado: medido, uma maquina com 100 L + 300 L (sem leitura)
       * + 100 L em 100 h aparecia como 1,0 L/h, quando o banco — e a verdade —
       * diziam 4,0. As duas metades da fracao tem que vir do mesmo lugar.
       *
       * O `??` cai para a conta local, que e a que vale quando a linha nao
       * passou pela view (nenhuma hoje, mas a tela nao depende disso). */
      const litrosDoIntervalo = abastecimento.litrosNoIntervalo ?? abastecimento.litros;
      const variacao = abastecimento.variacaoDoMedidor ?? horas;
      /* UMA MEDIA POR UNIDADE. A variacao de um caminhao e km e a de uma
         escavadeira e h: somadas no mesmo denominador, os km dos caminhoes
         inflavam as "horas" e a tela escrevia 1,6 L/h para maquinas que fazem
         de 13 a 20. */
      if (variacao !== null && variacao !== undefined && variacao > 0) {
        if (unidade === 'L/km') {
          litrosComBasePorKm += litrosDoIntervalo;
          kmComBase += variacao;
          comBaseDeConsumoPorKm += 1;
        } else {
          litrosComBase += litrosDoIntervalo;
          horasComBase += variacao;
          comBaseDeConsumo += 1;
        }
      }
    }

    if (lancamento.pendencias.length > 0) comPendencia += 1;

    const quando = abastecimento.quando;
    if (primeiro === null || quando.getTime() < primeiro.getTime()) primeiro = quando;
    if (ultimo === null || quando.getTime() > ultimo.getTime()) ultimo = quando;
  }

  return {
    quantidade: lancamentos.length,
    litros,
    custo,
    consumoMedio: horasComBase > 0 ? litrosComBase / horasComBase : null,
    comBaseDeConsumo,
    consumoMedioPorKm: kmComBase > 0 ? litrosComBasePorKm / kmComBase : null,
    comBaseDeConsumoPorKm,
    semPreco,
    comPendencia,
    primeiro,
    ultimo,
  };
}

export interface ConsumoPorAtivo {
  readonly codigo: string;
  readonly tipo: string | null;
  readonly litros: number;
  readonly lancamentos: number;
  /** Quantos lançamentos tinham base de comparação. O resto não entrou na conta. */
  readonly comBase: number;
  /** Média PONDERADA: litros com base divididos pelas horas (ou km) deles. */
  readonly consumoMedio: number | null;
  readonly unidade: string;
  readonly ultimoEm: Date | null;
  readonly faixaMinimo: number | null;
  readonly faixaMaximo: number | null;
}

/**
 * Agrega por máquina — e a média é PONDERADA, de propósito.
 *
 * A média das médias daria peso igual a um lançamento de 88 L e a um de 430 L,
 * o que produz um número errado com cara de certo.
 *
 * MORA AQUI, e não na tela que a desenha, porque ela é a gêmea de
 * `resumoDoPeriodo`: as duas aplicam a mesma regra de qual lançamento entra no
 * L/h, e foi exatamente esta — a média por máquina — que somou ARLA no diesel
 * até a `0011`. Dentro do `.tsx` nenhum teste a alcançava: o próprio
 * `react-refresh/only-export-components` recusa exportá-la de lá e manda pôr a
 * função num arquivo à parte. Regra que ninguém consegue medir é regra que
 * volta a quebrar com a suíte verde.
 */
export function agregarPorAtivo(
  lancamentos: readonly Lancamento[],
  equipamentos: readonly EquipamentoDoModulo[],
): readonly ConsumoPorAtivo[] {
  const porCodigo = new Map(equipamentos.map((e) => [e.codigo, e]));
  const acumulado = new Map<
    string,
    { litros: number; lancamentos: number; comBase: number; litrosComBase: number; base: number; ultimo: Date | null }
  >();

  /* A UNIDADE DA MÁQUINA, e só lançamento nela entra na média. É a do
     cadastro — o medidor de hoje, e a unidade em que a faixa foi escrita —;
     fora do cadastro (a máquina desativada, que `/combustivel/cadastros` não
     devolve), a do lançamento mais recente dela, que já vem do medidor da
     leitura. A tabela escrevia "L/h" para o caminhão desativado que o
     Lançamento mostra em L/km, e o lançamento de um medidor trocado somava km
     com horas na mesma média. */
  const maisRecente = new Map<string, Lancamento>();
  for (const lancamento of lancamentos) {
    const codigo = lancamento.abastecimento.equipamentoCodigo;
    const visto = maisRecente.get(codigo);
    if (visto === undefined || lancamento.abastecimento.quando.getTime() > visto.abastecimento.quando.getTime()) {
      maisRecente.set(codigo, lancamento);
    }
  }
  const unidadeDe = (codigo: string): UnidadeDeConsumo => {
    const equipamento = porCodigo.get(codigo);
    return equipamento ? unidadeDeConsumo(equipamento.tipoMedidor) : (maisRecente.get(codigo)?.unidade ?? 'L/h');
  };

  for (const lancamento of lancamentos) {
    const codigo = lancamento.abastecimento.equipamentoCodigo;
    const atual = acumulado.get(codigo) ?? {
      litros: 0,
      lancamentos: 0,
      comBase: 0,
      litrosComBase: 0,
      base: 0,
      ultimo: null,
    };

    atual.litros += lancamento.abastecimento.litros;
    atual.lancamentos += 1;
    /* SÓ COMBUSTÍVEL ENTRA NO L/h. O ARLA de uma máquina tem cadeia própria
       no banco (migração 0011), com o próprio intervalo de medidor: somá-lo
       aqui punha litros que não são do diesel no numerador e contava as mesmas
       horas duas vezes no denominador. A linha continua nos litros e na
       contagem de lançamentos — ela só não decide o consumo médio. */
    if (
      lancamento.consumo !== null &&
      lancamento.horas !== null &&
      lancamento.unidade === unidadeDe(codigo) &&
      entraNaMediaDeConsumo(lancamento.abastecimento)
    ) {
      atual.comBase += 1;
      atual.litrosComBase += lancamento.abastecimento.litros;
      atual.base += lancamento.horas;
    }
    const quando = lancamento.abastecimento.quando;
    if (atual.ultimo === null || quando.getTime() > atual.ultimo.getTime()) atual.ultimo = quando;

    acumulado.set(codigo, atual);
  }

  return [...acumulado.entries()]
    .map(([codigo, dados]) => {
      const equipamento = porCodigo.get(codigo) ?? null;
      return {
        codigo,
        tipo: equipamento?.tipo ?? null,
        litros: dados.litros,
        lancamentos: dados.lancamentos,
        comBase: dados.comBase,
        consumoMedio: dados.base > 0 ? dados.litrosComBase / dados.base : null,
        unidade: unidadeDe(codigo),
        ultimoEm: dados.ultimo,
        faixaMinimo: equipamento?.faixa?.minimo ?? null,
        faixaMaximo: equipamento?.faixa?.maximo ?? null,
      };
    })
    .sort((um, outro) => outro.litros - um.litros);
}
